import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ETAPES_INVESTISSEMENT } from '@eec/contrats';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { EnTetePage } from '../../commun/EnTetePage';
import { PiecesJointes } from './PiecesJointes';
interface Demande {
  precedent_id?: string | null;
  precedent_titre?: string | null;
  complements_demandes?: boolean;
  motif_avis?: string | null;
  correction_id?: string | null;
  instruction_id?: string | null;
  id: string;
  titre: string;
  justification: string;
  montant: string;
  equipement_id: string | null;
  statut: keyof typeof ETAPES_INVESTISSEMENT;
  version: number;
  transmis_le: string | null;
}
function Historique({ id }: { id: string }) {
  const q = useQuery({
    queryKey: ['investissements', 'historique', id],
    queryFn: () =>
      requete<{
        elements: {
          id: string;
          action: string;
          motif: string;
          contenu: Demande;
          cree_le: string;
        }[];
      }>(`/investissements-reseau/${id}/historique`),
  });
  return (
    <>
      {q.error && <p role="alert">{messageErreur(q.error)}</p>}
      <ol>
        {q.data?.elements.map((h) => (
          <li key={h.id}>
            {new Date(h.cree_le).toLocaleString('fr-FR')} · {h.action} · {h.motif}
            <details>
              <summary>Version conservée</summary>
              <p>
                {h.contenu.titre} · {BigInt(h.contenu.montant).toLocaleString('fr-FR')} XAF
              </p>
              <p style={{ whiteSpace: 'pre-wrap' }}>{h.contenu.justification}</p>
            </details>
          </li>
        ))}
      </ol>
    </>
  );
}
export function PageInvestissements() {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [edition, setEdition] = useState<Demande | 'nouveau' | null>(null);
  const [historique, setHistorique] = useState<string | null>(null);
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const q = useQuery({
    queryKey: ['investissements', 'liste'],
    queryFn: () => requete<{ elements: Demande[] }>('/investissements-reseau'),
    enabled: peut(contexte, 'investissement', 'voir'),
  });
  const equipements = useQuery({
    queryKey: ['patrimoine', 'equipements'],
    queryFn: () =>
      requete<{ elements: { id: string; nom: string; code: string }[] }>('/patrimoine/equipements'),
    enabled: !!edition && peut(contexte, 'patrimoine', 'voir'),
  });
  async function envoyer(chemin: string, corps: unknown) {
    setErreur('');
    setOccupe(true);
    try {
      const resultat = await requete<Demande>(chemin, { methode: 'POST', corps });
      await client.invalidateQueries({ queryKey: ['investissements'] });
      await client.invalidateQueries({ queryKey: ['instruction-investissements'] });
      setEdition(
        chemin.endsWith('/complements') && peut(contexte, 'investissement', 'modifier')
          ? resultat
          : null,
      );
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setOccupe(false);
    }
  }
  const selection = edition && edition !== 'nouveau' ? edition : null;
  if (!peut(contexte, 'investissement', 'voir'))
    return <p role="alert">Accès aux investissements non autorisé.</p>;
  return (
    <section>
      <EnTetePage titre="Demandes d’investissement" />
      <p>
        <Link to="/instruction-investissements">Consulter les avis et le suivi institutionnel</Link>
      </p>
      <p>
        Préparez une demande documentée pour transmission par la direction. La transmission
        enregistre la version officielle ; elle ne constitue ni une approbation de financement ni un
        envoi de courriel.
      </p>
      {peut(contexte, 'investissement', 'creer') && (
        <button onClick={() => setEdition('nouveau')}>Nouvelle demande</button>
      )}
      {erreur && <p role="alert">{erreur}</p>}
      {q.error && <p role="alert">{messageErreur(q.error)}</p>}
      {q.isPending && <p>Chargement des demandes…</p>}
      {edition && (
        <form
          key={selection?.id ?? 'nouveau'}
          style={{ display: 'grid', gap: 12, maxWidth: 750 }}
          onSubmit={(e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            void envoyer(
              selection
                ? `/investissements-reseau/${selection.id}/actions`
                : '/investissements-reseau',
              {
                titre: d.get('titre'),
                justification: d.get('justification'),
                montant: Number(d.get('montant')),
                equipement_id: d.get('equipement_id') || null,
                ...(selection
                  ? { action: 'reviser', version: selection.version, motif: d.get('motif') }
                  : {}),
              },
            );
          }}
        >
          <h2>{selection ? 'Réviser la demande' : 'Préparer un brouillon'}</h2>
          <label>
            Titre de la demande{' '}
            <input
              name="titre"
              required
              minLength={5}
              maxLength={200}
              defaultValue={selection?.titre}
            />
          </label>
          <label>
            Justification technique{' '}
            <textarea
              name="justification"
              required
              minLength={20}
              maxLength={8000}
              rows={6}
              defaultValue={selection?.justification}
            />
          </label>
          <label>
            Montant estimé en XAF{' '}
            <input
              name="montant"
              type="number"
              required
              min={0}
              max={100000000000}
              step={1}
              defaultValue={selection?.montant}
            />
          </label>
          <label>
            Équipement concerné{' '}
            <select name="equipement_id" defaultValue={selection?.equipement_id ?? ''}>
              <option value="">Aucun équipement spécifique</option>
              {equipements.data?.elements.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.code} — {x.nom}
                </option>
              ))}
            </select>
          </label>
          {equipements.error && <p role="alert">{messageErreur(equipements.error)}</p>}
          {selection && (
            <label>
              Motif de révision <input name="motif" required minLength={5} maxLength={2000} />
            </label>
          )}
          <button disabled={occupe || equipements.isFetching || !!equipements.error}>
            Enregistrer le brouillon
          </button>
          <button type="button" disabled={occupe} onClick={() => setEdition(null)}>
            Fermer le formulaire
          </button>
        </form>
      )}
      <p>Les 500 demandes les plus récentes de l’organisation sont affichées.</p>
      {q.data?.elements.length === 0 && <p>Aucune demande enregistrée.</p>}
      {q.data?.elements.map((d) => (
        <article
          id={d.id}
          key={d.id}
          style={{ paddingBlock: 20, borderBottom: '1px solid currentColor' }}
        >
          <h2>{d.titre}</h2>
          {d.precedent_id && (
            <p>
              Compléments à :{' '}
              <a href={`#${d.precedent_id}`}>{d.precedent_titre ?? 'demande précédente'}</a>.
              Joignez les justificatifs de cette nouvelle version ; les anciennes pièces restent
              consultables dans le dossier précédent.
            </p>
          )}
          <p>
            {ETAPES_INVESTISSEMENT[d.statut]} · {BigInt(d.montant).toLocaleString('fr-FR')} XAF
          </p>
          <p style={{ whiteSpace: 'pre-wrap' }}>{d.justification}</p>
          {d.transmis_le && (
            <p>Transmission enregistrée le {new Date(d.transmis_le).toLocaleString('fr-FR')}</p>
          )}
          {d.complements_demandes && (
            <p>
              <strong>Compléments demandés</strong> · {d.motif_avis}
            </p>
          )}
          {d.correction_id && (
            <p>
              <a href={`#${d.correction_id}`}>Consulter la version de complément</a>
            </p>
          )}
          {d.complements_demandes &&
            !d.correction_id &&
            peut(contexte, 'investissement', 'creer') && (
              <form
                style={{ display: 'grid', gap: 12, maxWidth: 750 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  void envoyer(`/investissements-reseau/${d.id}/complements`, {
                    version: d.version,
                    motif: new FormData(e.currentTarget).get('motif'),
                  });
                }}
              >
                <p>
                  Le dossier et son avis sont conservés. Un nouveau brouillon reprendra les
                  informations à compléter avant une nouvelle transmission par la direction.
                </p>
                <label>
                  Motif de préparation des compléments{' '}
                  <input name="motif" required minLength={5} maxLength={2000} />
                </label>
                <button disabled={occupe}>Préparer les compléments</button>
              </form>
            )}
          {d.statut === 'SUBMITTED' &&
            !d.instruction_id &&
            peut(contexte, 'investissement', 'transmettre') && (
              <button
                disabled={occupe}
                onClick={() => void envoyer(`/investissements-reseau/${d.id}/instruction`, {})}
              >
                Assurer le suivi institutionnel de cette demande
              </button>
            )}
          {d.statut === 'DRAFT' && (
            <>
              {peut(contexte, 'investissement', 'modifier') && (
                <button disabled={occupe} onClick={() => setEdition(d)}>
                  Réviser la demande
                </button>
              )}
              {(peut(contexte, 'investissement', 'modifier') ||
                peut(contexte, 'investissement', 'transmettre')) && (
                <form
                  style={{ display: 'grid', gap: 12, maxWidth: 750 }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const f = new FormData(e.currentTarget);
                    void envoyer(`/investissements-reseau/${d.id}/actions`, {
                      action: f.get('action'),
                      version: d.version,
                      motif: f.get('motif'),
                    });
                  }}
                >
                  <label>
                    Action sur la demande{' '}
                    <select name="action">
                      {peut(contexte, 'investissement', 'transmettre') && (
                        <option value="transmettre">Transmettre la demande</option>
                      )}
                      {peut(contexte, 'investissement', 'modifier') && (
                        <option value="annuler">Annuler le brouillon</option>
                      )}
                    </select>
                  </label>
                  <label>
                    Motif de l’action <input name="motif" required minLength={5} maxLength={2000} />
                  </label>
                  <button disabled={occupe}>Appliquer à la demande</button>
                </form>
              )}
            </>
          )}
          <PiecesJointes
            type="investissement"
            id={d.id}
            ecriture={d.statut === 'DRAFT' && peut(contexte, 'investissement', 'modifier')}
          />
          <button
            aria-expanded={historique === d.id}
            onClick={() => setHistorique(historique === d.id ? null : d.id)}
          >
            Historique de la demande
          </button>
          {historique === d.id && <Historique id={d.id} />}
        </article>
      ))}
    </section>
  );
}
